import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { Provider } from 'react-redux';

import { ErrorFallback } from '@dfq/shared/components/ErrorFallback';
import {
  installClientErrorReporting,
  reportCrashMarkerIfPresent,
  reportIfNew,
} from '@dfq/shared/lib/installClientErrorReporting';
import { Toaster } from '@dfq/shared/components/ui/sonner';
import { ApiError } from '@dfq/shared/api/_envelope';
import { store } from '@dfq/shared/stores/index';

import { StorefrontRouterProvider } from '@/router';

import './App.css';

/**
 * Singleton QueryClient for the Storefront SPA.
 *
 * staleTime: 60s — public catalog and CMS pages are infrequently updated.
 * retry: skip 404s (they won't resolve on retry); allow 1 retry for transient
 * errors like 500 / network failures.
 *
 * Individual queries inherit these defaults and may override them.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: (failCount, err) =>
        !(err instanceof ApiError && err.status === 404) && failCount < 1,
    },
  },
});

// Installed at module scope rather than in an effect: a crash during the
// very first render happens before any effect runs, and that is exactly the
// failure worth capturing.
installClientErrorReporting('storefront');
// Reports a crash marker left by the previous load (SH-CONTOUR-107) before
// anything else runs.
reportCrashMarkerIfPresent('storefront');

function App() {
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, info) => reportIfNew('storefront', error, 'render', info.componentStack ?? undefined)}
    >
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <StorefrontRouterProvider />
          {/* SH-INFRA-006 — single global <Toaster> at the root. */}
          <Toaster />
        </QueryClientProvider>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
